export type DebugVerbosity = "STANDARD" | "VERBOSE";
export type DebugDiagnosticsDetail = "OFF" | "CORE" | "FULL";

export interface DebugSettings {
  enabled: boolean;
  overlayGameplay: boolean;
  overlayBot: boolean;
  verbosity: DebugVerbosity;
  diagnostics: DebugDiagnosticsDetail;
  perfSafeMode: boolean;
}

export const DEFAULT_DEBUG_SETTINGS: DebugSettings = {
  enabled: false,
  overlayGameplay: true,
  overlayBot: true,
  verbosity: "STANDARD",
  diagnostics: "CORE",
  perfSafeMode: false
};

const VERBOSITY_OPTIONS: readonly DebugVerbosity[] = ["STANDARD", "VERBOSE"];
const DIAGNOSTICS_OPTIONS: readonly DebugDiagnosticsDetail[] = ["OFF", "CORE", "FULL"];

function isVerbosity(value: unknown): value is DebugVerbosity {
  return typeof value === "string" && VERBOSITY_OPTIONS.includes(value as DebugVerbosity);
}

function isDiagnostics(value: unknown): value is DebugDiagnosticsDetail {
  return typeof value === "string" && DIAGNOSTICS_OPTIONS.includes(value as DebugDiagnosticsDetail);
}

export function normalizeDebugSettings(settings: Partial<DebugSettings> | null | undefined): DebugSettings {
  return {
    enabled: settings?.enabled === true,
    overlayGameplay: settings?.overlayGameplay !== false,
    overlayBot: settings?.overlayBot !== false,
    verbosity: isVerbosity(settings?.verbosity) ? settings.verbosity : DEFAULT_DEBUG_SETTINGS.verbosity,
    diagnostics: isDiagnostics(settings?.diagnostics) ? settings.diagnostics : DEFAULT_DEBUG_SETTINGS.diagnostics,
    perfSafeMode: settings?.perfSafeMode === true
  };
}

export function enforceDebugAccess(
  settings: Partial<DebugSettings> | null | undefined,
  canUseDebugSettings: boolean
): DebugSettings {
  const normalized = normalizeDebugSettings(settings);
  if (canUseDebugSettings) {
    return normalized;
  }
  return {
    ...normalized,
    enabled: false
  };
}
